[
  {
    "slug": "add-business-follow-up-task",
    "title": "Add Business Follow-Up Task",
    "icon": "fa-square-check",
    "agent": "customer-success-manager",
    "promptTemplate": "Add a follow-up task to {{business}} to do {{followUpWork}}. Use the existing Platfio business, notes, and tasks to avoid duplicates, then create the task with the right priority, due timing, and owner context for {{reason}}.",
    "promptParts": [
      {
        "type": "text",
        "value": "Add a follow-up task to "
      },
      {
        "type": "field",
        "value": "business"
      },
      {
        "type": "text",
        "value": " to do "
      },
      {
        "type": "field",
        "value": "followUpWork"
      },
      {
        "type": "text",
        "value": ". Use the existing Platfio business, notes, and tasks to avoid duplicates, then create the task with the right priority, due timing, and owner context for "
      },
      {
        "type": "field",
        "value": "reason"
      },
      {
        "type": "text",
        "value": "."
      }
    ],
    "recommendedModel": "openai/gpt-5-mini",
    "recommendedToolGroups": [
      "businesses",
      "tasks",
      "notes"
    ],
    "systemPromptOverride": "You are the Customer Success Manager agent running the Add Business Follow-Up Task playbook inside Platfio.\n\nThis playbook exists because it performs a useful agency operation in Platfio. Do not turn it into generic consulting, generic strategy, or a padded checklist. Start from the relevant Platfio records and inspect only the tools needed for this job.\n\nDo the work: inspect existing business tasks/notes for duplicates, then create a clear follow-up task attached to the business with action, reason, priority, and due timing. If enough context and permissions are available, create or update the appropriate Platfio businesses, contacts, notes, tasks, tickets, plans, proposals, previews, or app records. If not, state exactly what is missing. Avoid destructive changes unless the user explicitly asks.\n\nReturn: task created or ready to create, duplicate check, business context used, note to add if helpful, and any missing owner/due-date context.",
    "fields": [
      {
        "id": "business",
        "label": "business",
        "type": "context",
        "placeholder": "select business",
        "required": true,
        "contextType": "business",
        "defaultValue": "selected business",
        "newLabel": "New business"
      },
      {
        "id": "followUpWork",
        "label": "follow-up work",
        "type": "text",
        "placeholder": "send the client a proposal reminder and ask for missing brand assets",
        "required": true
      },
      {
        "id": "reason",
        "label": "reason",
        "type": "text",
        "placeholder": "keep the deal moving after discovery",
        "required": true
      }
    ],
    "description": "Create a concrete follow-up task on a Platfio business after checking existing tasks and notes."
  },
  {
    "slug": "agency-funnel-summary",
    "title": "Agency Funnel Summary",
    "icon": "fa-filter-circle-dollar",
    "agent": "marketing",
    "promptTemplate": "Summarize the funnels set up in this Platfio agency for {{summaryGoal}}. List the funnels, identify what each appears to be for, render funnel previews for the most important or unclear funnels, and recommend cleanup or follow-up tasks only where useful.",
    "promptParts": [
      {
        "type": "text",
        "value": "Summarize the funnels set up in this Platfio agency for "
      },
      {
        "type": "field",
        "value": "summaryGoal"
      },
      {
        "type": "text",
        "value": ". List the funnels, identify what each appears to be for, render funnel previews for the most important or unclear funnels, and recommend cleanup or follow-up tasks only where useful."
      }
    ],
    "recommendedModel": "openai/gpt-5-mini",
    "recommendedToolGroups": [
      "funnels",
      "preview",
      "notes",
      "tasks"
    ],
    "systemPromptOverride": "You are the Marketing agent running the Agency Funnel Summary playbook inside Platfio.\n\nThis playbook exists because it performs a useful agency operation in Platfio. Do not turn it into generic consulting, generic strategy, or a padded checklist. Start from the relevant Platfio records and inspect only the tools needed for this job.\n\nDo the work: list agency funnels, summarize their likely purpose from top-level fields, render funnel previews where useful, and identify practical cleanup or follow-up work. If enough context and permissions are available, create or update the appropriate Platfio businesses, contacts, notes, tasks, tickets, plans, proposals, previews, or app records. If not, state exactly what is missing. Avoid destructive changes unless the user explicitly asks.\n\nReturn: funnel inventory, likely purpose by funnel, previews rendered or needed, stale/unclear funnels, and recommended tasks.",
    "fields": [
      {
        "id": "summaryGoal",
        "label": "summary goal",
        "type": "text",
        "placeholder": "understand current lead capture and sales flows",
        "required": true
      }
    ],
    "description": "Summarize the funnels configured in a Platfio agency and preview important or unclear funnel routes."
  },
  {
    "slug": "business-lead-enrichment",
    "title": "Business Lead Enrichment",
    "icon": "fa-address-card",
    "agent": "sdr",
    "promptTemplate": "Enrich the Platfio lead record for {{business}}. Research {{researchFocus}} on the web, inspect existing business/contact records, then add useful notes and follow-up tasks for {{salesGoal}} without inventing facts.",
    "promptParts": [
      {
        "type": "text",
        "value": "Enrich the Platfio lead record for "
      },
      {
        "type": "field",
        "value": "business"
      },
      {
        "type": "text",
        "value": ". Research "
      },
      {
        "type": "field",
        "value": "researchFocus"
      },
      {
        "type": "text",
        "value": " on the web, inspect existing business/contact records, then add useful notes and follow-up tasks for "
      },
      {
        "type": "field",
        "value": "salesGoal"
      },
      {
        "type": "text",
        "value": " without inventing facts."
      }
    ],
    "recommendedModel": "openai/gpt-5-mini",
    "recommendedToolGroups": [
      "businesses",
      "businessContacts",
      "web",
      "notes",
      "tasks"
    ],
    "systemPromptOverride": "You are the SDR agent running the Business Lead Enrichment playbook inside Platfio.\n\nThis playbook exists because it performs a useful agency operation in Platfio. Do not turn it into generic consulting, generic strategy, or a padded checklist. Start from the selected Platfio business and inspect only the records needed for this job.\n\nDo the work: research the business on the web, compare findings to Platfio CRM/contact records, and add only useful sourced notes and next sales tasks. If enough context and permissions are available, create or update the appropriate Platfio notes, tasks, tickets, plans, proposals, or records. If not, state exactly what is missing. Avoid destructive changes unless the user explicitly asks.\n\nReturn: sourced lead facts, CRM gaps, business note content, follow-up tasks, and discovery-call angles.",
    "fields": [
      {
        "id": "business",
        "label": "business",
        "type": "context",
        "placeholder": "select business",
        "required": true,
        "contextType": "business",
        "defaultValue": "selected business",
        "newLabel": "New business"
      },
      {
        "id": "researchFocus",
        "label": "research focus",
        "type": "text",
        "placeholder": "their website, services, locations, pricing signals, and likely app opportunities",
        "required": true
      },
      {
        "id": "salesGoal",
        "label": "sales goal",
        "type": "text",
        "placeholder": "prepare a more specific discovery call",
        "required": true
      }
    ],
    "description": "Research a business lead on the web, enrich its Platfio CRM record, add useful notes, and create follow-up tasks."
  },
  {
    "slug": "software-proposal-generator",
    "title": "Create Proposal From Plan",
    "icon": "fa-file-signature",
    "agent": "account-executive",
    "promptTemplate": "Create or update a Platfio proposal for {{business}} from the selected plan/proposal context. They are {{buyer}}. They need {{offer}} built with {{platform}}, optimized around {{goal}}. Use CRM, plan, proposal, payment, note, and task context before drafting.",
    "promptParts": [
      {
        "type": "text",
        "value": "Create or update a Platfio proposal for "
      },
      {
        "type": "field",
        "value": "business"
      },
      {
        "type": "text",
        "value": " from the selected plan/proposal context. They are "
      },
      {
        "type": "field",
        "value": "buyer"
      },
      {
        "type": "text",
        "value": ". They need "
      },
      {
        "type": "field",
        "value": "offer"
      },
      {
        "type": "text",
        "value": " built with "
      },
      {
        "type": "field",
        "value": "platform"
      },
      {
        "type": "text",
        "value": ", optimized around "
      },
      {
        "type": "field",
        "value": "goal"
      },
      {
        "type": "text",
        "value": ". Use CRM, plan, proposal, payment, note, and task context before drafting."
      }
    ],
    "recommendedModel": "openai/gpt-5.4",
    "recommendedToolGroups": [
      "businesses",
      "businessContacts",
      "plan",
      "proposal",
      "businessPayments",
      "notes",
      "tasks"
    ],
    "systemPromptOverride": "You are the Account Executive agent running the Create Proposal From Plan playbook inside Platfio.\n\nThis playbook exists because it performs a useful agency operation in Platfio. Do not turn it into generic consulting, generic strategy, or a padded checklist. Start from the selected Platfio business and inspect only the records needed for this job.\n\nDo the work: turn Platfio plan and CRM context into a proposal with scope, package/milestone structure, pricing framing, payment readiness, and next tasks. If enough context and permissions are available, create or update the appropriate Platfio notes, tasks, tickets, plans, proposals, or records. If not, state exactly what is missing. Avoid destructive changes unless the user explicitly asks.\n\nReturn: proposal draft or update plan, assumptions, Platfio proposal sections to update, payment/signature blockers, and client next-step message.",
    "fields": [
      {
        "id": "business",
        "label": "business",
        "type": "context",
        "placeholder": "select business",
        "required": true,
        "contextType": "business",
        "defaultValue": "selected business",
        "newLabel": "New business"
      },
      {
        "id": "buyer",
        "label": "business type",
        "type": "select",
        "placeholder": "select a business type",
        "required": true,
        "allowCustom": true,
        "options": [
          {
            "label": "a healthcare provider",
            "value": "a healthcare provider"
          },
          {
            "label": "a real estate agency",
            "value": "a real estate agency"
          },
          {
            "label": "a construction company",
            "value": "a construction company"
          },
          {
            "label": "a financial services firm",
            "value": "a financial services firm"
          },
          {
            "label": "a retail or e-commerce store",
            "value": "a retail or e-commerce store"
          },
          {
            "label": "an education provider",
            "value": "an education provider"
          },
          {
            "label": "a hospitality business",
            "value": "a hospitality business"
          },
          {
            "label": "a professional services firm",
            "value": "a professional services firm"
          },
          {
            "label": "a trades and home services business",
            "value": "a trades and home services business"
          },
          {
            "label": "a fitness or wellness studio",
            "value": "a fitness or wellness studio"
          },
          {
            "label": "a legal practice",
            "value": "a legal practice"
          }
        ]
      },
      {
        "id": "offer",
        "label": "project type",
        "type": "select",
        "placeholder": "select a project type",
        "required": true,
        "options": [
          {
            "label": "a Platfio app",
            "value": "a Platfio app"
          },
          {
            "label": "a client portal",
            "value": "a client portal"
          },
          {
            "label": "a website plus app",
            "value": "a website plus app"
          },
          {
            "label": "an automation-backed business app",
            "value": "an automation-backed business app"
          },
          {
            "label": "an AI-assisted service workflow",
            "value": "an AI-assisted service workflow"
          }
        ]
      },
      {
        "id": "platform",
        "label": "platform or technology",
        "type": "select",
        "placeholder": "select a platform",
        "required": true,
        "options": [
          {
            "label": "Platfio",
            "value": "Platfio"
          },
          {
            "label": "WordPress",
            "value": "WordPress"
          },
          {
            "label": "Shopify",
            "value": "Shopify"
          },
          {
            "label": "Wix",
            "value": "Wix"
          },
          {
            "label": "Webflow",
            "value": "Webflow"
          },
          {
            "label": "Squarespace",
            "value": "Squarespace"
          },
          {
            "label": "Lovable",
            "value": "Lovable"
          },
          {
            "label": "Replit",
            "value": "Replit"
          }
        ]
      },
      {
        "id": "goal",
        "label": "optimize proposal around",
        "type": "multi-select",
        "placeholder": "select business cases",
        "required": true,
        "allowCustom": true,
        "options": [
          {
            "label": "faster operations",
            "value": "faster operations"
          },
          {
            "label": "more bookings or purchases",
            "value": "more bookings or purchases"
          },
          {
            "label": "better customer self-service",
            "value": "better customer self-service"
          },
          {
            "label": "recurring revenue",
            "value": "recurring revenue"
          },
          {
            "label": "reduced manual admin",
            "value": "reduced manual admin"
          },
          {
            "label": "cleaner reporting",
            "value": "cleaner reporting"
          },
          {
            "label": "compliance and security",
            "value": "compliance and security"
          }
        ]
      }
    ],
    "description": "Create or update a Platfio proposal from plan, CRM, payment, note, and task context."
  },
  {
    "slug": "customer-lifetime-value-assessment",
    "title": "Customer Lifetime Value Assessment",
    "icon": "fa-chart-line",
    "agent": "finance-billing",
    "promptTemplate": "Assess customer lifetime value for {{business}} over {{timeframe}}. Fetch Platfio invoices, subscriptions, payment history, purchases, proposals, projects, tasks, and notes, then calculate what they have spent, what is recurring, what is outstanding, and what follow-up is warranted for {{assessmentGoal}}.",
    "promptParts": [
      {
        "type": "text",
        "value": "Assess customer lifetime value for "
      },
      {
        "type": "field",
        "value": "business"
      },
      {
        "type": "text",
        "value": " over "
      },
      {
        "type": "field",
        "value": "timeframe"
      },
      {
        "type": "text",
        "value": ". Fetch Platfio invoices, subscriptions, payment history, purchases, proposals, projects, tasks, and notes, then calculate what they have spent, what is recurring, what is outstanding, and what follow-up is warranted for "
      },
      {
        "type": "field",
        "value": "assessmentGoal"
      },
      {
        "type": "text",
        "value": "."
      }
    ],
    "recommendedModel": "openai/gpt-5-mini",
    "recommendedToolGroups": [
      "businesses",
      "businessPayments",
      "purchases",
      "proposal",
      "projects",
      "tasks",
      "notes"
    ],
    "systemPromptOverride": "You are the Finance Billing agent running the Customer Lifetime Value Assessment playbook inside Platfio.\n\nThis playbook exists because it performs a useful agency operation in Platfio. Do not turn it into generic consulting, generic strategy, or a padded checklist. Start from the selected Platfio business and inspect only the records needed for this job.\n\nDo the work: calculate spend and account value from Platfio payment/invoice/subscription/purchase records, then connect that to proposals, projects, tasks, and notes. If enough context and permissions are available, create or update the appropriate Platfio notes, tasks, tickets, plans, proposals, or records. If not, state exactly what is missing. Avoid destructive changes unless the user explicitly asks.\n\nReturn: amount spent, recurring value, outstanding invoices or payment risk, expansion/renewal signals, recommended tasks/notes, and assumptions.",
    "fields": [
      {
        "id": "business",
        "label": "business",
        "type": "context",
        "placeholder": "select business",
        "required": true,
        "contextType": "business",
        "defaultValue": "selected business",
        "newLabel": "New business"
      },
      {
        "id": "timeframe",
        "label": "timeframe",
        "type": "text",
        "placeholder": "all available history",
        "required": true
      },
      {
        "id": "assessmentGoal",
        "label": "assessment goal",
        "type": "text",
        "placeholder": "decide whether to prioritize renewal, expansion, or collections",
        "required": true
      }
    ],
    "description": "Assess a customer account from Platfio invoices, subscriptions, purchases, proposals, projects, tasks, and notes."
  },
  {
    "slug": "discovery-notes-to-follow-up",
    "title": "Discovery Notes To Follow-Up",
    "icon": "fa-notes-medical",
    "agent": "account-executive",
    "promptTemplate": "Here are discovery notes for {{business}}: {{discoveryNotes}}. Add the substance to the Platfio business, decide whether the next follow-up should be a task, plan, or proposal, and draft the client follow-up for {{outcome}}.",
    "promptParts": [
      {
        "type": "text",
        "value": "Here are discovery notes for "
      },
      {
        "type": "field",
        "value": "business"
      },
      {
        "type": "text",
        "value": ": "
      },
      {
        "type": "field",
        "value": "discoveryNotes"
      },
      {
        "type": "text",
        "value": ". Add the substance to the Platfio business, decide whether the next follow-up should be a task, plan, or proposal, and draft the client follow-up for "
      },
      {
        "type": "field",
        "value": "outcome"
      },
      {
        "type": "text",
        "value": "."
      }
    ],
    "recommendedModel": "openai/gpt-5-mini",
    "recommendedToolGroups": [
      "businesses",
      "businessContacts",
      "notes",
      "tasks",
      "plan",
      "proposal"
    ],
    "systemPromptOverride": "You are the Account Executive agent running the Discovery Notes To Follow-Up playbook inside Platfio.\n\nThis playbook exists because it performs a useful agency operation in Platfio. Do not turn it into generic consulting, generic strategy, or a padded checklist. Start from the selected Platfio business and inspect only the records needed for this job.\n\nDo the work: turn raw discovery notes into a useful Platfio business note plus the right next follow-up record: task, plan, or proposal. If enough context and permissions are available, create or update the appropriate Platfio notes, tasks, tickets, plans, proposals, or records. If not, state exactly what is missing. Avoid destructive changes unless the user explicitly asks.\n\nReturn: business note, recommended next artifact, task/proposal/plan action, client follow-up message, and missing context.",
    "fields": [
      {
        "id": "business",
        "label": "business",
        "type": "context",
        "placeholder": "select business",
        "required": true,
        "contextType": "business",
        "defaultValue": "selected business",
        "newLabel": "New business"
      },
      {
        "id": "discoveryNotes",
        "label": "discovery notes",
        "type": "text",
        "placeholder": "client wants booking, payments, staff scheduling, and customer portal support",
        "required": true
      },
      {
        "id": "outcome",
        "label": "desired outcome",
        "type": "text",
        "placeholder": "move them into paid discovery or proposal review",
        "required": true
      }
    ],
    "description": "Turn discovery notes into a Platfio business note and the right follow-up: task, plan, or proposal."
  },
  {
    "slug": "discovery-notes-to-plan-preview",
    "title": "Discovery Notes To Plan Preview",
    "icon": "fa-clipboard-question",
    "agent": "product-manager",
    "promptTemplate": "Here are discovery notes for {{business}}: {{discoveryNotes}}. Add the notes to the Platfio business, create or update a Platfio plan around {{planningGoal}}, then render a plan preview so the agency can review it.",
    "promptParts": [
      {
        "type": "text",
        "value": "Here are discovery notes for "
      },
      {
        "type": "field",
        "value": "business"
      },
      {
        "type": "text",
        "value": ": "
      },
      {
        "type": "field",
        "value": "discoveryNotes"
      },
      {
        "type": "text",
        "value": ". Add the notes to the Platfio business, create or update a Platfio plan around "
      },
      {
        "type": "field",
        "value": "planningGoal"
      },
      {
        "type": "text",
        "value": ", then render a plan preview so the agency can review it."
      }
    ],
    "recommendedModel": "openai/gpt-5-mini",
    "recommendedToolGroups": [
      "businesses",
      "businessContacts",
      "notes",
      "files",
      "plan",
      "tasks",
      "preview"
    ],
    "systemPromptOverride": "You are the Product Manager agent running the Discovery Notes To Plan Preview playbook inside Platfio.\n\nThis playbook exists because it performs a useful agency operation in Platfio. Do not turn it into generic consulting, generic strategy, or a padded checklist. Start from the relevant Platfio records and inspect only the tools needed for this job.\n\nDo the work: capture the discovery notes on the business, create or update a Platfio Plan Builder plan with discovery, personas, stories, flows, constraints, and module choices, then render a plan preview. If enough context and permissions are available, create or update the appropriate Platfio businesses, contacts, notes, tasks, tickets, plans, proposals, previews, or app records. If not, state exactly what is missing. Avoid destructive changes unless the user explicitly asks.\n\nReturn: business note, plan created or updated, plan sections populated, missing questions/tasks, preview rendered or exact preview blocker.",
    "fields": [
      {
        "id": "business",
        "label": "business",
        "type": "context",
        "placeholder": "select business",
        "required": true,
        "contextType": "business",
        "defaultValue": "selected business",
        "newLabel": "New business"
      },
      {
        "id": "discoveryNotes",
        "label": "discovery notes",
        "type": "text",
        "placeholder": "client wants booking, payments, staff scheduling, and customer portal support",
        "required": true
      },
      {
        "id": "planningGoal",
        "label": "planning goal",
        "type": "text",
        "placeholder": "create a client-shareable app plan",
        "required": true
      }
    ],
    "description": "Add discovery notes to a business, create or update a Platfio plan, and render a plan preview."
  },
  {
    "slug": "draft-support-ticket-response",
    "title": "Draft Support Ticket Response",
    "icon": "fa-reply",
    "agent": "customer-success-manager",
    "promptTemplate": "Draft a support ticket response for {{business}} about {{ticketContext}}. Inspect the ticket history, ticket messages, related notes, and open tasks, render the ticket preview if useful, then draft a client-safe response and the internal follow-up needed for {{responseGoal}}.",
    "promptParts": [
      {
        "type": "text",
        "value": "Draft a support ticket response for "
      },
      {
        "type": "field",
        "value": "business"
      },
      {
        "type": "text",
        "value": " about "
      },
      {
        "type": "field",
        "value": "ticketContext"
      },
      {
        "type": "text",
        "value": ". Inspect the ticket history, ticket messages, related notes, and open tasks, render the ticket preview if useful, then draft a client-safe response and the internal follow-up needed for "
      },
      {
        "type": "field",
        "value": "responseGoal"
      },
      {
        "type": "text",
        "value": "."
      }
    ],
    "recommendedModel": "openai/gpt-5-mini",
    "recommendedToolGroups": [
      "businesses",
      "tickets",
      "tasks",
      "notes",
      "preview"
    ],
    "systemPromptOverride": "You are the Customer Success Manager agent running the Draft Support Ticket Response playbook inside Platfio.\n\nThis playbook exists because it performs a useful agency operation in Platfio. Do not turn it into generic consulting, generic strategy, or a padded checklist. Start from the relevant Platfio records and inspect only the tools needed for this job.\n\nDo the work: inspect the selected business, relevant tickets/messages, notes, tasks, and ticket preview before drafting a response; draft only unless the user explicitly asks to send. If enough context and permissions are available, create or update the appropriate Platfio businesses, contacts, notes, tasks, tickets, previews, or records. If not, state exactly what is missing. Avoid destructive changes unless the user explicitly asks.\n\nReturn: ticket summary, client-ready response draft, internal follow-up task/note, risks or missing context, and whether the response should be sent manually.",
    "fields": [
      {
        "id": "business",
        "label": "business",
        "type": "context",
        "placeholder": "select business",
        "required": true,
        "contextType": "business",
        "defaultValue": "selected business",
        "newLabel": "New business"
      },
      {
        "id": "ticketContext",
        "label": "ticket context",
        "type": "text",
        "placeholder": "ticket ID or summary of the client issue",
        "required": true
      },
      {
        "id": "responseGoal",
        "label": "response goal",
        "type": "text",
        "placeholder": "acknowledge the issue, explain next steps, and keep trust",
        "required": true
      }
    ],
    "description": "Draft a client-safe support ticket response from Platfio ticket history, notes, tasks, and business context."
  },
  {
    "slug": "new-business-lead-create-and-enrich",
    "title": "New Business Lead Create And Enrich",
    "icon": "fa-building-circle-plus",
    "agent": "sdr",
    "promptTemplate": "Create a new Platfio business lead for {{businessName}} from {{leadSource}}. Research the business on the web, add useful contacts or contact clues where available, capture these extra notes: {{extraNotes}}, then create the business, add enrichment notes, and create the next follow-up task for {{salesGoal}}.",
    "promptParts": [
      {
        "type": "text",
        "value": "Create a new Platfio business lead for "
      },
      {
        "type": "field",
        "value": "businessName"
      },
      {
        "type": "text",
        "value": " from "
      },
      {
        "type": "field",
        "value": "leadSource"
      },
      {
        "type": "text",
        "value": ". Research the business on the web, add useful contacts or contact clues where available, capture these extra notes: "
      },
      {
        "type": "field",
        "value": "extraNotes"
      },
      {
        "type": "text",
        "value": ", then create the business, add enrichment notes, and create the next follow-up task for "
      },
      {
        "type": "field",
        "value": "salesGoal"
      },
      {
        "type": "text",
        "value": "."
      }
    ],
    "recommendedModel": "openai/gpt-5-mini",
    "recommendedToolGroups": [
      "web",
      "businesses",
      "businessContacts",
      "notes",
      "tasks"
    ],
    "systemPromptOverride": "You are the SDR agent running the New Business Lead Create And Enrich playbook inside Platfio.\n\nThis playbook exists because it performs a useful agency operation in Platfio. Do not turn it into generic consulting, generic strategy, or a padded checklist. Start from the relevant Platfio records and inspect only the tools needed for this job.\n\nDo the work: create a new business lead, research it on the web, enrich the CRM record with sourced notes and contacts/contact clues, include the user-provided extra notes, and create a next follow-up task. If enough context and permissions are available, create or update the appropriate Platfio businesses, contacts, notes, tasks, tickets, previews, or records. If not, state exactly what is missing. Avoid destructive changes unless the user explicitly asks.\n\nReturn: business created or duplicate found, sourced enrichment facts, contacts/contact clues, notes added, follow-up task, and uncertainties.",
    "fields": [
      {
        "id": "businessName",
        "label": "business name",
        "type": "text",
        "placeholder": "Acme Pilates",
        "required": true
      },
      {
        "id": "leadSource",
        "label": "lead source",
        "type": "text",
        "placeholder": "website URL, referral, LinkedIn page, or rough lead note",
        "required": true
      },
      {
        "id": "extraNotes",
        "label": "extra notes",
        "type": "text",
        "placeholder": "met at event, interested in bookings and memberships",
        "required": true
      },
      {
        "id": "salesGoal",
        "label": "sales goal",
        "type": "text",
        "placeholder": "book a discovery call",
        "required": true
      }
    ],
    "description": "Create a new Platfio business lead, research it, add extra notes, enrich contacts, and create a follow-up task."
  },
  {
    "slug": "outbound-business-prospecting",
    "title": "Outbound Business Prospecting",
    "icon": "fa-magnifying-glass-location",
    "agent": "sdr",
    "promptTemplate": "Find 10 outbound prospects in {{region}} that match {{businessType}}. Search the web, capture sourced business names, websites, locations, and contact clues, then add them as Platfio businesses with useful notes and follow-up tasks for {{outboundGoal}}.",
    "promptParts": [
      {
        "type": "text",
        "value": "Find 10 outbound prospects in "
      },
      {
        "type": "field",
        "value": "region"
      },
      {
        "type": "text",
        "value": " that match "
      },
      {
        "type": "field",
        "value": "businessType"
      },
      {
        "type": "text",
        "value": ". Search the web, capture sourced business names, websites, locations, and contact clues, then add them as Platfio businesses with useful notes and follow-up tasks for "
      },
      {
        "type": "field",
        "value": "outboundGoal"
      },
      {
        "type": "text",
        "value": "."
      }
    ],
    "recommendedModel": "openai/gpt-5-mini",
    "recommendedToolGroups": [
      "web",
      "businesses",
      "businessContacts",
      "notes",
      "tasks"
    ],
    "systemPromptOverride": "You are the SDR agent running the Outbound Business Prospecting playbook inside Platfio.\n\nThis playbook exists because it performs a useful agency operation in Platfio. Do not turn it into generic consulting, generic strategy, or a padded checklist. Start from the relevant Platfio records and inspect only the tools needed for this job.\n\nDo the work: search the web for 10 relevant businesses in the selected region/type, verify each from public sources, create Platfio business records, add sourced notes/contact clues, and create sensible follow-up tasks. If enough context and permissions are available, create or update the appropriate Platfio businesses, contacts, notes, tasks, tickets, plans, proposals, previews, or app records. If not, state exactly what is missing. Avoid destructive changes unless the user explicitly asks.\n\nReturn: 10 prospects found, source URLs, businesses created or skipped with reason, notes added, follow-up tasks, and duplicates/uncertainties.",
    "fields": [
      {
        "id": "region",
        "label": "region",
        "type": "text",
        "placeholder": "Perth, Western Australia",
        "required": true
      },
      {
        "id": "businessType",
        "label": "business type",
        "type": "text",
        "placeholder": "pilates studios that could use booking and membership apps",
        "required": true
      },
      {
        "id": "outboundGoal",
        "label": "outbound goal",
        "type": "text",
        "placeholder": "book discovery calls for Platfio app opportunities",
        "required": true
      }
    ],
    "description": "Search the web for 10 outbound prospects, add them as Platfio businesses, and create useful notes and follow-up tasks."
  },
  {
    "slug": "platform-app-database-summary",
    "title": "Platform App Database Summary",
    "icon": "fa-database",
    "agent": "full-stack-engineering",
    "promptTemplate": "Summarize what is built in the Platfio platform app for {{business}}. Check the apps on the business, choose the Platfio/platform app if one exists, inspect its database types and records for products, bookables, subscribables, forms, users, purchases, submissions, and automations, then explain what the app generally does.",
    "promptParts": [
      {
        "type": "text",
        "value": "Summarize what is built in the Platfio platform app for "
      },
      {
        "type": "field",
        "value": "business"
      },
      {
        "type": "text",
        "value": ". Check the apps on the business, choose the Platfio/platform app if one exists, inspect its database types and records for products, bookables, subscribables, forms, users, purchases, submissions, and automations, then explain what the app generally does."
      }
    ],
    "recommendedModel": "openai/gpt-5-mini",
    "recommendedToolGroups": [
      "businesses",
      "apps",
      "platfioApp",
      "preview",
      "notes"
    ],
    "systemPromptOverride": "You are the Full Stack Engineering agent running the Platform App Database Summary playbook inside Platfio.\n\nThis playbook exists because it performs a useful agency operation in Platfio. Do not turn it into generic consulting, generic strategy, or a padded checklist. Start from the relevant Platfio records and inspect only the tools needed for this job.\n\nDo the work: read the business apps, identify the Platfio/platform app, inspect app data types and representative records, especially products, bookables, subscribables, forms, users, purchases, submissions, and automation-related records, then summarize the app as built. If enough context and permissions are available, create or update the appropriate Platfio businesses, contacts, notes, tasks, tickets, plans, proposals, previews, or app records. If not, state exactly what is missing. Avoid destructive changes unless the user explicitly asks.\n\nReturn: apps found, platform app selected, data types inspected, what the app appears to do, notable modules/content, missing or empty records, and suggested follow-up notes.",
    "fields": [
      {
        "id": "business",
        "label": "business",
        "type": "context",
        "placeholder": "select business",
        "required": true,
        "contextType": "business",
        "defaultValue": "selected business",
        "newLabel": "New business"
      }
    ],
    "description": "Inspect a business, find its Platfio platform app, read key app database records, and summarize what the app does."
  },
  {
    "slug": "portal-handover-preview",
    "title": "Portal Handover Preview",
    "icon": "fa-person-chalkboard",
    "agent": "customer-success-manager",
    "promptTemplate": "Prepare a handover preview for {{business}} before {{handoverMoment}}. Inspect the Platfio handover configuration, portal steps, onboarding fields, files, and tasks, then render the portal handover preview and list anything that must be fixed before sending it to the client.",
    "promptParts": [
      {
        "type": "text",
        "value": "Prepare a handover preview for "
      },
      {
        "type": "field",
        "value": "business"
      },
      {
        "type": "text",
        "value": " before "
      },
      {
        "type": "field",
        "value": "handoverMoment"
      },
      {
        "type": "text",
        "value": ". Inspect the Platfio handover configuration, portal steps, onboarding fields, files, and tasks, then render the portal handover preview and list anything that must be fixed before sending it to the client."
      }
    ],
    "recommendedModel": "openai/gpt-5-mini",
    "recommendedToolGroups": [
      "businesses",
      "businessHandover",
      "files",
      "tasks",
      "preview"
    ],
    "systemPromptOverride": "You are the Customer Success Manager agent running the Portal Handover Preview playbook inside Platfio.\n\nThis playbook exists because it performs a useful agency operation in Platfio. Do not turn it into generic consulting, generic strategy, or a padded checklist. Start from the relevant Platfio records and inspect only the tools needed for this job.\n\nDo the work: inspect business handover configuration, portal steps, onboarding fields, visible files, and tasks, then render_preview with previewType portal for the selected business. If enough context and permissions are available, create or update the appropriate Platfio businesses, contacts, notes, tasks, tickets, plans, proposals, previews, or app records. If not, state exactly what is missing. Avoid destructive changes unless the user explicitly asks.\n\nReturn: handover readiness, portal preview rendered or exact blocker, issues to fix, tasks to create/update, and client handover message points.",
    "fields": [
      {
        "id": "business",
        "label": "business",
        "type": "context",
        "placeholder": "select business",
        "required": true,
        "contextType": "business",
        "defaultValue": "selected business",
        "newLabel": "New business"
      },
      {
        "id": "handoverMoment",
        "label": "handover moment",
        "type": "text",
        "placeholder": "sending the onboarding portal to the client",
        "required": true
      }
    ],
    "description": "Inspect a Platfio business handover setup and render the portal handover preview before client handoff."
  },
  {
    "slug": "signed-proposal-to-project",
    "title": "Signed Proposal To Project",
    "icon": "fa-folder-plus",
    "agent": "project-manager",
    "promptTemplate": "A proposal just signed for {{business}}: {{proposal}}. Generate the Platfio project kickoff for {{kickoffGoal}}, including milestones, tasks, payment/handover checks, owner assignments, and the first client update.",
    "promptParts": [
      {
        "type": "text",
        "value": "A proposal just signed for "
      },
      {
        "type": "field",
        "value": "business"
      },
      {
        "type": "text",
        "value": ": "
      },
      {
        "type": "field",
        "value": "proposal"
      },
      {
        "type": "text",
        "value": ". Generate the Platfio project kickoff for "
      },
      {
        "type": "field",
        "value": "kickoffGoal"
      },
      {
        "type": "text",
        "value": ", including milestones, tasks, payment/handover checks, owner assignments, and the first client update."
      }
    ],
    "recommendedModel": "openai/gpt-5-mini",
    "recommendedToolGroups": [
      "businesses",
      "proposal",
      "projects",
      "tasks",
      "businessHandover",
      "businessPayments",
      "notes"
    ],
    "systemPromptOverride": "You are the Project Manager agent running the Signed Proposal To Project playbook inside Platfio.\n\nThis playbook exists because it performs a useful agency operation in Platfio. Do not turn it into generic consulting, generic strategy, or a padded checklist. Start from the selected Platfio business and inspect only the records needed for this job.\n\nDo the work: convert a signed Platfio proposal into a project structure, milestones, tasks, handover checks, payment checks, owner assignments, and kickoff communication. If enough context and permissions are available, create or update the appropriate Platfio notes, tasks, tickets, plans, proposals, or records. If not, state exactly what is missing. Avoid destructive changes unless the user explicitly asks.\n\nReturn: project setup, milestone/task list, payment and handover blockers, owner assignments, and client kickoff note.",
    "fields": [
      {
        "id": "business",
        "label": "business",
        "type": "context",
        "placeholder": "select business",
        "required": true,
        "contextType": "business",
        "defaultValue": "selected business",
        "newLabel": "New business"
      },
      {
        "id": "proposal",
        "label": "proposal",
        "type": "context",
        "placeholder": "select proposal",
        "required": true,
        "contextType": "proposal",
        "dependsOn": "business",
        "defaultValue": "selected proposal",
        "newLabel": "New proposal"
      },
      {
        "id": "kickoffGoal",
        "label": "kickoff goal",
        "type": "text",
        "placeholder": "start delivery without scope or onboarding ambiguity",
        "required": true
      }
    ],
    "description": "Generate a Platfio project kickoff from a signed proposal, including tasks, handover checks, payment checks, and client communication."
  },
  {
    "slug": "support-ticket-history-review",
    "title": "Support Ticket History Review",
    "icon": "fa-headset",
    "agent": "customer-success-manager",
    "promptTemplate": "Review the support ticket history for {{business}} over {{timeframe}}. Inspect tickets, ticket messages, tasks, notes, and project context, then summarize recurring issues, open risks, owner actions, and the client update for {{reviewGoal}}.",
    "promptParts": [
      {
        "type": "text",
        "value": "Review the support ticket history for "
      },
      {
        "type": "field",
        "value": "business"
      },
      {
        "type": "text",
        "value": " over "
      },
      {
        "type": "field",
        "value": "timeframe"
      },
      {
        "type": "text",
        "value": ". Inspect tickets, ticket messages, tasks, notes, and project context, then summarize recurring issues, open risks, owner actions, and the client update for "
      },
      {
        "type": "field",
        "value": "reviewGoal"
      },
      {
        "type": "text",
        "value": "."
      }
    ],
    "recommendedModel": "openai/gpt-5-mini",
    "recommendedToolGroups": [
      "businesses",
      "tickets",
      "tasks",
      "notes",
      "projects"
    ],
    "systemPromptOverride": "You are the Customer Success Manager agent running the Support Ticket History Review playbook inside Platfio.\n\nThis playbook exists because it performs a useful agency operation in Platfio. Do not turn it into generic consulting, generic strategy, or a padded checklist. Start from the selected Platfio business and inspect only the records needed for this job.\n\nDo the work: review ticket history and related Platfio tasks/notes/projects to find recurring support themes, unresolved risks, and owner follow-up. If enough context and permissions are available, create or update the appropriate Platfio notes, tasks, tickets, plans, proposals, or records. If not, state exactly what is missing. Avoid destructive changes unless the user explicitly asks.\n\nReturn: ticket history summary, recurring issues, open risks, owner tasks, recommended notes/tickets to update, and client-ready update.",
    "fields": [
      {
        "id": "business",
        "label": "business",
        "type": "context",
        "placeholder": "select business",
        "required": true,
        "contextType": "business",
        "defaultValue": "selected business",
        "newLabel": "New business"
      },
      {
        "id": "timeframe",
        "label": "timeframe",
        "type": "text",
        "placeholder": "the last 90 days",
        "required": true
      },
      {
        "id": "reviewGoal",
        "label": "review goal",
        "type": "text",
        "placeholder": "prepare for a client health check-in",
        "required": true
      }
    ],
    "description": "Review a client business support history from Platfio tickets, messages, tasks, notes, and project context."
  }
]
